import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { of, tap, switchMap, map } from 'rxjs';
import { FieldExpression, NodeExpression, toString as luceneToString } from 'lucene';

import { ORDER_SERVER_URL } from '../../config';
import { PaginatedResult } from '../../shared/models/paginated-result';
import { batchCardFields, ClaimBatch, FieldOfClaimBatch } from '../models/claim-batch';
import { OrderByDirection, OrderBy } from '../../shared/utils/constant';

import { AqdbService } from './aqdb.service';

const GET_BATCHES_URL = `${ ORDER_SERVER_URL }/v4/claim-batches`;

@Injectable({ providedIn: 'root' })
export class QualityIssueBatchService {
	private batch?: ClaimBatch;

	constructor(
		private readonly http: HttpClient,
		private readonly aqdbService: AqdbService
	) {}

	getBatchCount(filter: string) {
		const url = new URL(GET_BATCHES_URL);

		const params = url.searchParams;
		params.append('filter', filter);
		params.append('onlyCount', 'true');

		return this.http.get<PaginatedResult<ClaimBatch>>(url.toString());
	}

	getBatches(
		fields: readonly FieldOfClaimBatch[],
		filter: string | undefined,
		index: number,
		limit: number,
		orderBy: OrderBy<keyof ClaimBatch>[] | OrderBy<keyof ClaimBatch>
	) {
		const url = new URL(GET_BATCHES_URL);
		const params = url.searchParams;
		params.append('fields', fields.join(','));
		if (filter) {
			params.append('filter', filter);
		}
		params.append('page', String(Math.floor(index / limit) + 1));
		params.append('limit', String(limit));

		params.append('orderBy', Array.isArray(orderBy) ? orderBy.join(',') : String(orderBy));

		return this.http.get<PaginatedResult<ClaimBatch>>(url.toString());
	}

	getBatchCard(batchId: string) {
		if (this.batch && this.batch.id === batchId) {
			return of(this.batch);
		}
		const url = new URL(`${ GET_BATCHES_URL }/${ batchId }`);
		const params = url.searchParams;
		params.append('fields', batchCardFields.join(','));
		return this.http.get<ClaimBatch>(url.toString()).pipe(
			tap(batch => {
				this.batch = batch;
			})
		);
	}

	getBatchCardByBarcode(barcode: string) {
		return this.aqdbService.getTrackingBatches(
			barcode,
			['millBatchReference', 'mill', 'supplierOrderNumber', 'supplierOrderItemNumber']
		)
			.pipe(switchMap(res => {
				if (res.count === 0) {
					throw new Error(`${ barcode } not found`);
				}
				const [trackingBatch] = res.results;
				const millBatchReferenceField: FieldExpression = {
					field: 'millBatchReference',
					term: trackingBatch.millBatchReference,
					quoted: false,
				};
				const supplierOrderNumberField: FieldExpression = {
					field: 'supplierOrderNumber',
					term: trackingBatch.supplierOrderNumber,
					quoted: false
				};
				const supplierOrderItemNumberField: FieldExpression = {
					field: 'supplierOrderItemNumber',
					term: trackingBatch.supplierOrderItemNumber,
					quoted: false
				};

				const allNode: NodeExpression = {
					left: millBatchReferenceField,
					operator: 'AND',
					parenthesized: false,
					right: {
						left: supplierOrderNumberField,
						operator: 'AND',
						parenthesized: false,
						right: supplierOrderItemNumberField
					}
				};

				return this.getBatches(
					batchCardFields,
					luceneToString(allNode),
					0,
					1,
					new OrderBy('cdd', OrderByDirection.DESC)
				)
					.pipe(
						map(batches => {
							if (batches.results.length < 1) {
								throw new Error('batch not found');
							}
							return batches.results[0];
						})
					);
			}));
	}
}
